import { Weather, Forecast } from './../classes/weather';
import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DOMParser } from 'xmldom';
import { TransferState, makeStateKey } from '@angular/platform-browser';
import { Observable, of, throwError } from 'rxjs';
import { map, shareReplay, catchError, timeout } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';

@Injectable({ providedIn: 'root' })
export class WeatherService {
  constructor(
    private http: HttpClient,
    private transferState: TransferState,
    @Inject(PLATFORM_ID) private platformId: any
  ) {}

  getCurrentTemperature$(locationString: string, locationiD?: string): Observable<Weather> {
    const weather = new Weather();
    const key = makeStateKey<string>('currentTemp|' + (locationiD || locationString));
    const cached = this.transferState.get(key, null as any);
    if (cached) {
      this.handleTemperatureResponse(cached, weather);
      return of(weather);
    }

    const date = new Date();
    date.setHours(date.getHours() - 1);
    let url = '';
    if (locationiD !== undefined && locationiD.length > 0) {
      url =
        'https://opendata.fmi.fi/wfs/fin?service=WFS&version=2.0.0&request=getFeature&storedquery_id=fmi::observations::weather::timevaluepair&fmisid=' +
        locationiD;
    } else {
      url =
        'https://opendata.fmi.fi/wfs/fin?service=WFS&version=2.0.0&request=getFeature&storedquery_id=fmi::observations::weather::timevaluepair&place=' +
        locationString;
    }

    return this.http.get(url + '&parameters=t2m&starttime=' + date.toISOString(), { responseType: 'text' }).pipe(
      timeout(5000),
      map(response => {
        this.handleTemperatureResponse(response, weather);
        try { this.transferState.set(key, response); } catch (e) {}
        return weather;
      }),
      catchError(() => of(weather)),
      shareReplay(1)
    );
  }

  getForecast$(locationString: string): Observable<Forecast[]> {
    const key = makeStateKey<string>('forecast|' + locationString);
    const cached = this.transferState.get(key, null as any);
    if (cached) {
      return of(this.parseForecast(cached));
    }

    const date = new Date();
    date.setHours(date.getHours() + 30);

    const url =
      'https://opendata.fmi.fi/wfs/fin?service=WFS&version=2.0.0&request=getFeature&storedquery_id=fmi::forecast::harmonie::surface::point::timevaluepair' +
      '&place=' + encodeURIComponent(locationString) +
      '&parameters=WeatherSymbol3,temperature,Precipitation1h,WindSpeedMS' +
      '&endtime=' + date.toISOString();

    return this.http.get(url, { responseType: 'text' }).pipe(
      timeout(5000),
      map(response => {
        try { this.transferState.set(key, response); } catch (e) {}
        return this.parseForecast(response);
      }),
      catchError(() => of([] as Forecast[])),
      shareReplay(1)
    );
  }

  // NOTE: Promise wrappers removed — use Observable APIs `getForecast$` and `getCurrentTemperature$`

  private handleTemperatureResponse(response: string, weather: Weather) {
    const xmlDoc = new DOMParser().parseFromString(response, 'text/xml');
    const elements = xmlDoc.getElementsByTagName('wml2:MeasurementTVP');

    if (elements.length > 0) {
      const valueElements = elements.item(elements.length - 1).getElementsByTagName('wml2:value');
      if (valueElements.length > 0) {
        weather.Temperature = valueElements[0].textContent;
      }
    }
  }

  private parseForecast(response: string): Forecast[] {
    const xmlDoc = new DOMParser().parseFromString(response, 'text/xml');
    const timeSeriesElements: any[] = Array.from(xmlDoc.getElementsByTagName('wml2:MeasurementTimeseries') || []);

    const byTime = new Map<number, Partial<Forecast>>();

    timeSeriesElements.forEach(item => {
      if (!item || !item.getAttribute) { return; }
      const id = (item.getAttribute('gml:id') || '').toString();
      const elements: any[] = Array.from(item.getElementsByTagName('wml2:MeasurementTVP') || []);

      elements.forEach(elementItem => {
        if (!elementItem) { return; }
        const tEl = elementItem.getElementsByTagName('wml2:time')[0];
        const vEl = elementItem.getElementsByTagName('wml2:value')[0];
        if (!tEl || !vEl) { return; }

        const time = new Date(tEl.textContent || '').getTime();
        if (Number.isNaN(time)) { return; }
        const value = (vEl.textContent || '').trim();

        const existing = byTime.get(time) || {};

        if (id.includes('Precipitation1h')) {
          existing.Precipitation = value;
        } else if (id.includes('WeatherSymbol3')) {
          existing.WeatherState = value;
          existing.WeatherSymbolUrl = this.getWeatherSymbol(value);
        } else if (id.toLowerCase().includes('temperature')) {
          if (!isNaN(Number(value))) {
            existing.Temperature = (Math.round(Number(value) * 10) / 10).toString();
          }
        } else if (id.includes('WindSpeedMS')) {
          if (!isNaN(Number(value))) {
            existing.WindSpeed = Math.round(Number(value)).toString();
          }
        }

        existing.Time = new Date(time);
        byTime.set(time, existing);
      });
    });

    const result = Array.from(byTime.values()).map(part => {
      const f = new Forecast();
      if (part.Time) { f.Time = part.Time as Date; }
      f.Precipitation = part.Precipitation || '';
      f.Temperature = part.Temperature || '';
      f.WindSpeed = part.WindSpeed || '';
      f.WeatherState = part.WeatherState || '';
      f.WeatherSymbolUrl = part.WeatherSymbolUrl || this.getWeatherSymbol(part.WeatherState || '');
      return f;
    });

    return result.sort((a, b) => a.Time.getTime() - b.Time.getTime());
  }

  getWeatherSymbol(weatherState: string): string {
    const numberSymbol = Number(weatherState);
    if (Number.isFinite(numberSymbol)) {
      return '/assets/symbols/' + numberSymbol.toString() + '.svg';
    }
    return '/assets/symbols/3.svg';
  }
}
